import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const whatsappNumber = '55999260502';

function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, subtotal, getWhatsAppMessage } = useCart();
  const navigate = useNavigate();

  const handleClose = () => setIsOpen(false);

  const handleWhatsApp = () => {
    const message = getWhatsAppMessage();
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleContinueShopping = () => {
    setIsOpen(false);
    navigate('/', { state: { scrollTo: 'catalogo' } });
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] transition-opacity"
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-background-50 z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
            <h2 className="font-heading text-lg font-semibold text-foreground-900">
              Seu Carrinho
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors"
              aria-label="Fechar carrinho"
            >
              <i className="ri-close-line text-xl text-foreground-700" />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {items.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                  <i className="ri-shopping-bag-line text-2xl text-foreground-500" />
                </div>
                <p className="text-sm text-foreground-600 mb-2">Seu carrinho está vazio</p>
                <p className="text-xs text-foreground-500">
                  Adicione produtos do catálogo e finalize pelo WhatsApp
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={`${item.productId}-${item.size}`}
                    className="flex gap-3 bg-background-100 rounded-xl p-3"
                  >
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-16 h-20 rounded-lg object-cover shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground-900 truncate">
                        {item.productName}
                      </h4>
                      <p className="text-xs text-foreground-500 mb-1">
                        {item.categoryLabel} — Tamanho: {item.size}
                      </p>
                      {item.price && (
                        <p className="text-sm font-medium text-primary-700">
                          R$ {item.price.toFixed(2)}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.size, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors"
                        >
                          <i className="ri-subtract-line text-xs" />
                        </button>
                        <span className="text-sm font-medium text-foreground-900 w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.size, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors"
                        >
                          <i className="ri-add-line text-xs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(item.productId, item.size)}
                          className="ml-auto w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          aria-label="Remover"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="px-5 py-4 border-t border-background-200/70 bg-background-50">
              {subtotal > 0 && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-foreground-600">Subtotal</span>
                  <span className="text-base font-semibold text-foreground-900">
                    R$ {subtotal.toFixed(2)}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={handleWhatsApp}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
              >
                <i className="ri-whatsapp-line" />
                Finalizar pedido pelo WhatsApp
              </button>
              <button
                type="button"
                onClick={handleContinueShopping}
                className="w-full mt-2.5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 border border-background-200 transition-colors"
              >
                <i className="ri-shopping-bag-2-line" />
                Continuar Comprando
              </button>
              <p className="text-xs text-foreground-500 text-center mt-2">
                Enviaremos disponibilidade, pagamento e entrega
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CartDrawer;